import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { fmtUsd, fmtUsdSigned } from '@/components/cards/cardFormat';
import { apiFetch } from '@/stores/authStore';
import { useTickerFeedNeeds } from '@/hooks/useTickerFeedNeeds';
import { useMarketStore } from '@/stores/marketStore';
import { useDisplaySettings } from '@/stores/displaySettings';
import { polyPositionLiveData } from '@/lib/polymarket/describeOrder';
import { estimateAggregateFeeCents, takerRateForTicker } from '@/lib/pricing/fees';
import { estimatePolyOrderFeeCents } from '@/lib/pricing/polyFees';
import { usePolyFeeSchedules } from '@/hooks/usePolyFeeSchedules';
import type { PolyFeeSchedule } from '@/lib/polymarket/relay';

export interface OpenPositionLite {
  /** Absent only on stale payloads from a pre-venue relay; those are Kalshi. */
  venue?: 'kalshi' | 'polymarket';
  ticker: string;
  side: 'yes' | 'no';
  netCount: number;
  avgEntryPriceCents: number;
  lastFillAt?: string | null;
  /** Exact basis in cents from the relay ledger; older relays omit it. */
  costBasisCents?: number;
  feesPaidCents?: number;
  /** Opening fees on the OPEN share only (relay-allocated). This — never
   *  feesPaidCents (the whole bucket incl. sold-share + sell fees) — is the
   *  fee component an unrealized recipe subtracts; using the bucket total
   *  double-counts the sold share against realized-net (Jul-14 audit). */
  openFeesCents?: number;
  /** Authoritative lifetime bucket totals. Optional for stale relay payloads;
   * OrgDetail withholds the full-position strip unless all three reconcile. */
  totalBoughtCount?: number;
  totalSoldCount?: number;
  totalBuyCostCents?: number;
}

export interface OrgTraderRow {
  userId: string;
  username: string;
  openCount: number;
  openNotionalCents: number;
  /** Gross trading + settlement P&L before fees. */
  realizedPnlCents: number;
  realizedFeesCents?: number | null;
  realizedPnlNetCents?: number | null;
  openOrderCount?: number;
  openPositions?: OpenPositionLite[];
}

interface OrgTradersTableProps {
  orgQueryParam?: string;
  onRowClick?: (row: OrgTraderRow) => void;
  highlightUserId?: string;
  title?: string;
  /** When set, renders an "Org account →" link in the card header — the drill
   *  into the org's full balance + positions/orders view (/positions/org/:id). */
  onViewOrgAccount?: () => void;
}

// Comma-grouping via the shared card formatter (five-figure org P&L).
function formatDollarsSigned(cents: number): string {
  return fmtUsdSigned(cents / 100);
}

export interface OrgTraderUnrealizedResult {
  /** Null unless every open position has a live mark. A partial sum must not
   *  look like a complete account result. */
  valueCents: number | null;
  /** Diagnostic subtotal over the positions that do have marks. Never render
   *  this as the account total when `complete` is false. */
  pricedValueCents: number;
  pricedPositions: number;
  totalPositions: number;
  complete: boolean;
}

/** Match the trader headline: both venues, exact basis, OPEN-share entry
 *  fees, and the Kalshi taker-close estimate. Any missing mark — or an
 *  unknown open-fee allocation — withholds the headline while preserving
 *  explicit marked/total coverage for the UI and tests. Open-share fees
 *  (openFeesCents), not the bucket's feesPaidCents: composing realized-NET
 *  with bucket-total fees double-subtracts the sold share on every
 *  partially-closed position. */
export function computeOrgTraderUnrealized(
  row: OrgTraderRow,
  getKalshiYesPrice: (ticker: string) => number | null,
  getPolyNowPrice: (ticker: string, side: 'yes' | 'no') => number | null =
    (ticker, side) => polyPositionLiveData(ticker, side).nowCents,
  getPolyFeeSchedule: (ticker: string) => PolyFeeSchedule | null | undefined = () => undefined,
): OrgTraderUnrealizedResult {
  const positions = row.openPositions;
  if (!positions || positions.length === 0) {
    return {
      valueCents: 0,
      pricedValueCents: 0,
      pricedPositions: 0,
      totalPositions: 0,
      complete: true,
    };
  }
  let total = 0;
  let pricedPositions = 0;
  for (const p of positions) {
    const venue = p.venue ?? 'kalshi';
    let current: number | null;
    if (venue === 'polymarket') {
      current = getPolyNowPrice(p.ticker, p.side);
    } else {
      const yesPrice = getKalshiYesPrice(p.ticker);
      current = yesPrice == null ? null : p.side === 'yes' ? yesPrice : 100 - yesPrice;
    }
    if (current == null) continue;
    // Unknown open-fee allocation = incomplete, same as a missing mark —
    // withhold rather than price the position as if its entry fees were $0.
    if (p.openFeesCents == null) continue;
    const entryCost =
      (p.costBasisCents ?? p.avgEntryPriceCents * p.netCount) + p.openFeesCents;
    let closingFee = 0;
    if (venue === 'kalshi') {
      const takerRate = takerRateForTicker(p.ticker);
      if (takerRate != null) {
        closingFee = estimateAggregateFeeCents({
          priceCents: current,
          count: p.netCount,
          feeRate: takerRate,
        });
      }
    } else {
      const estimated = estimatePolyOrderFeeCents({
        priceCents: current,
        count: p.netCount,
        role: 'taker',
        schedule: getPolyFeeSchedule(p.ticker),
      });
      if (estimated == null) continue;
      closingFee = estimated;
    }
    pricedPositions++;
    total += current * p.netCount - closingFee - entryCost;
  }
  const complete = pricedPositions === positions.length;
  return {
    valueCents: complete ? total : null,
    pricedValueCents: total,
    pricedPositions,
    totalPositions: positions.length,
    complete,
  };
}

export function OrgTradersTable({
  orgQueryParam = '',
  onRowClick,
  highlightUserId,
  title = 'Org Positions by Trader',
  onViewOrgAccount,
}: OrgTradersTableProps) {
  const [rows, setRows] = useState<OrgTraderRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Same masking the positions tiles honor (default hidden) — without this a
  // manager sees ••• tiles above a table of everyone's live P&L dollars.
  const showPnl = useDisplaySettings((s) => s.showPnl);
  // Consumer feed filters (feed-filters-per-view): every open position in the
  // org, by identity, so the manager's marks ride the edge at browser cadence
  // without the connection carrying the sports they sit in. Undeclared until
  // the first fetch lands (null) — a loading table has nothing to name.
  const orgFeedIds = useMemo(
    () => (loading && rows.length === 0 ? null : rows.flatMap((row) => (row.openPositions ?? []).map((p) => p.ticker))),
    [rows, loading],
  );
  useTickerFeedNeeds('org-traders-table', orgFeedIds, { fold: 'view' });
  const polyFeeConditionIds = useMemo(
    () =>
      rows.flatMap((row) =>
        (row.openPositions ?? [])
          .filter((position) => position.venue === 'polymarket')
          .map((position) => position.ticker),
      ),
    [rows],
  );
  const { schedules: polyFeeSchedules } = usePolyFeeSchedules(polyFeeConditionIds);

  // Re-derive Unrealized P&L when hydra ticks update prices.
  const marketVersion = useMarketStore((s) => s.version);
  const getMarketPrice = useMarketStore((s) => s.getMarketPrice);
  void marketVersion;

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/api/manager/org/positions-by-trader${orgQueryParam}`);
      const data = await res.json();
      if (!res.ok) {
        setError(data?.error ?? 'Failed to load');
        setRows([]);
      } else {
        setRows(data.rows ?? []);
      }
    } catch {
      setError('Network error');
    }
    setLoading(false);
  }, [orgQueryParam]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  return (
    <div className="mt-6 space-y-2">
      {/* Section header in the page background — matches the Positions / Open
          Orders / Bots headers on this page; the card below is just the table. */}
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-foreground text-sm font-semibold">{title}</h2>
        <div className="flex items-center gap-3">
          {onViewOrgAccount && (
            <button
              onClick={onViewOrgAccount}
              title="Open this org's account (balance + all positions/orders)"
              className="text-muted-foreground hover:text-primary text-[11px] underline-offset-2 hover:underline"
            >
              Org account →
            </button>
          )}
          <button
            onClick={fetchRows}
            title="Refresh"
            className="text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px]"
          >
            <RefreshCw size={10} /> Refresh
          </button>
        </div>
      </div>
      <div className="bg-card border-border overflow-x-auto rounded-lg border">
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
          </div>
        ) : error ? (
          <p className="text-muted-foreground px-4 py-4 text-xs">{error}</p>
        ) : rows.length === 0 ? (
          <p className="text-muted-foreground px-4 py-4 text-xs">No activity yet.</p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-muted-foreground border-border border-b text-[11px] font-medium uppercase tracking-wider">
                <th className="px-4 py-2 text-left">Member</th>
                <th className="px-3 py-2 text-right">Open Contracts</th>
                <th className="px-3 py-2 text-right">Open Orders (K)</th>
                <th className="px-3 py-2 text-right">Open Notional</th>
                <th className="px-3 py-2 text-right">Unrealized (K+P)</th>
                <th
                  className="px-3 py-2 text-right"
                  title="Trading and settlement P&L before fees"
                >
                  Realized Gross
                </th>
                <th
                  className="px-3 py-2 text-right"
                  title="Actual recorded fees allocated to sold and settled contracts"
                >
                  Realized Fees
                </th>
                <th
                  className="px-3 py-2 text-right"
                  title="Realized Gross minus Realized Fees; dash means contributing fee data is incomplete"
                >
                  Realized Net
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isHighlight = r.userId === highlightUserId;
                const clickable = !!onRowClick;
                const unrealized = computeOrgTraderUnrealized(
                  r,
                  getMarketPrice,
                  undefined,
                  (ticker) => polyFeeSchedules.get(ticker),
                );
                return (
                  <tr
                    key={r.userId}
                    onClick={clickable ? () => onRowClick(r) : undefined}
                    className={`border-border/40 border-b ${
                      clickable ? 'hover:bg-muted/30 cursor-pointer' : ''
                    } ${isHighlight ? 'bg-muted/40' : ''}`}
                  >
                    <td className="text-foreground px-4 py-2">
                      {r.username}
                      {isHighlight && (
                        <span className="text-muted-foreground ml-2 text-[10px]">(you)</span>
                      )}
                    </td>
                    <td className="text-foreground px-3 py-2 text-right font-mono">{r.openCount}</td>
                    <td className="text-foreground px-3 py-2 text-right font-mono">
                      {r.openOrderCount ?? 0}
                    </td>
                    <td className="text-foreground px-3 py-2 text-right font-mono">
                      {fmtUsd(r.openNotionalCents / 100)}
                    </td>
                    <td
                      className={`px-3 py-2 text-right font-mono font-medium ${
                        !showPnl || unrealized.valueCents == null
                          ? 'text-muted-foreground'
                          : unrealized.valueCents >= 0
                            ? 'text-green-500'
                            : 'text-red-500'
                      }`}
                      title={
                        unrealized.complete
                          ? `${unrealized.pricedPositions}/${unrealized.totalPositions} positions fully priced`
                          : `Unrealized P&L withheld: ${unrealized.pricedPositions}/${unrealized.totalPositions} positions have both a live mark and close-fee schedule`
                      }
                    >
                      {unrealized.valueCents == null
                        ? `— (${unrealized.pricedPositions}/${unrealized.totalPositions})`
                        : showPnl
                          ? formatDollarsSigned(unrealized.valueCents)
                          : '•••'}
                    </td>
                    <td
                      className={`px-3 py-2 text-right font-mono font-medium ${
                        !showPnl
                          ? 'text-muted-foreground'
                          : r.realizedPnlCents >= 0
                            ? 'text-green-500'
                            : 'text-red-500'
                      }`}
                    >
                      {showPnl ? formatDollarsSigned(r.realizedPnlCents) : '•••'}
                    </td>
                    <td className="text-muted-foreground px-3 py-2 text-right font-mono">
                      {r.realizedFeesCents == null
                        ? '—'
                        : showPnl
                          ? fmtUsd(r.realizedFeesCents / 100)
                          : '•••'}
                    </td>
                    <td
                      className={`px-3 py-2 text-right font-mono font-medium ${
                        !showPnl || r.realizedPnlNetCents == null
                          ? 'text-muted-foreground'
                          : r.realizedPnlNetCents >= 0
                            ? 'text-green-500'
                            : 'text-red-500'
                      }`}
                    >
                      {r.realizedPnlNetCents == null
                        ? '—'
                        : showPnl
                          ? formatDollarsSigned(r.realizedPnlNetCents)
                          : '•••'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
